import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Grid,
  Icon,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ThemeContext } from 'context/ThemeContext';
// import Search from '../SideBar/Search';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, SidebarItem } from 'types/types';
import { setActiveView } from 'features/applicationState';
import { useNavigate } from 'react-router-dom';
// import OpenedViews from './OpenedViews';
import { generateQueryParams, getSidebarItemProp } from 'utils/Utils';
import { NiKendoContext } from 'context/NiKendoContext';
import useView from 'hooks/useView';
import MaxOpenedViews from './MaxOpenedViews';

export default function SideBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const maxHeightValueRef = useRef(90);
  const { niKendoModule }: any = useContext(NiKendoContext);
  const { sidebarItems, toggle, searchText, activeView } = useSelector(
    (state: RootState) => state.applicationState
  );
  const { closeOpenView, openView } = useView();
  const activeViewName =
    activeView?.title || activeView?.text || activeView?.viewId || 'ViewName';
  // Add an event listener to receive updates from Kendo
  useEffect(() => {
    niKendoModule.application.layout.addEventListener(
      'setActiveView',
      (view) => {
        dispatch(setActiveView({ ...view, value: view.viewId }));
      }
    );

    niKendoModule.application.layout.addEventListener(
      'closeKendoView',
      (viewId) => {
        closeOpenView({ value: viewId });
      }
    );

    niKendoModule.application.layout.addEventListener(
      'openKendoView',
      (viewId) => {
        openView(viewId);
      }
    );
    // Remove listenesr when your component unmounts
    return () => {
      niKendoModule.application.layout.removeEventListener('setActiveView');
      niKendoModule.application.layout.removeEventListener('closeKendoView');
      niKendoModule.application.layout.removeEventListener('openKendoView');
    };
  }, [dispatch, niKendoModule.application.layout, closeOpenView, openView]);

  const handleItemClick = (
    clickedItem: SidebarItem,
    clickedItemKey: string
  ) => {
    var externalLink = getSidebarItemProp(clickedItem, 'external_link', '');
    if (externalLink) {
      // Open the external link in a new tab
      window.open(externalLink, '_blank');
      return;
    }
    if (
      clickedItem.type === 'VIEW' ||
      clickedItem.type === 'DYNAMICVIEW' ||
      clickedItem.type === 'DASHBOARD'
    ) {
      navigate(
        `/${clickedItem.type.toLowerCase()}/${clickedItem.value.toLowerCase()}?${generateQueryParams(
          clickedItem.properties
        )}`
      );
    } else if (toggle) {
      handleExpandToggle(clickedItemKey, clickedItem.isExpanded);
      // dispatch(toggleMenu());
    } else {
      handleExpandToggle(clickedItemKey, clickedItem.isExpanded);
    }
  };

  const filterSidebarItemsRecursively = useCallback(
    (items: SidebarItem[], searchText: string) => {
      return items?.filter((item) => {
        const isMatchingText = item.text
          .toLowerCase()
          .includes(searchText.toLowerCase());
        if (isMatchingText) return true;

        if (item.items && item.items.length > 0) {
          // Recursively filter sub-items
          const filteredItems = filterSidebarItemsRecursively(
            item.items,
            searchText
          );
          if (filteredItems.length > 0) {
            item.isExpanded = true;
            item.items = filteredItems;
            return true;
          }
        }
        return isMatchingText;
      });
    },
    []
  );

  const filteredSidebarItems = useMemo(
    () =>
      filterSidebarItemsRecursively(structuredClone(sidebarItems), searchText),
    [filterSidebarItemsRecursively, sidebarItems, searchText]
  );

  const [expandStates, setExpandStates] = useState<{
    [key: string]: boolean;
  }>({});

  const handleExpandToggle = (key: string, isExpanded: boolean) => {
    setExpandStates((prevStates) => ({
      ...prevStates,
      [key]: prevStates[key] == undefined ? !isExpanded : !prevStates[key],
    }));
  };

  const renderSidebarItems = (
    items: SidebarItem[],
    level: number,
    parentKey: string
  ) => {
    return items.map((item, index) => {
      const nestedLevel = level + 1;
      const itemKey = `${parentKey}-${index}`;
      const isInitiallyOpen = expandStates[itemKey] || item.isExpanded;

      const IconComponent =
        item.iconClass && level == 0 ? (
          <Icon
            key={index}
            className={`is-icon-main is-icon ${item.iconClass}`}
          />
        ) : (
          <></>
        );
      return (
        <React.Fragment key={itemKey}>
          <ListItem
            className={`menu-button-level-${level} ${
              item.items.length === 0 ? 'menu-button-last' : ''
            } ${
              isInitiallyOpen || item.text === activeViewName
                ? 'active-menu'
                : ''
            }`}
            button
            onClick={() => handleItemClick(item, itemKey)}
          >
            <>
              {IconComponent}
              <ListItemText
                primary={item.text}
                className={`list-item-text ${
                  item.iconClass ? '' : 'last-list-item-text'
                }`}
              />
            </>
            {item.items && item.items.length > 0 && (
              <ListItemIcon>
                {isInitiallyOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemIcon>
            )}
          </ListItem>
          {item.items && item.items.length > 0 && (
            <Collapse in={isInitiallyOpen}>
              <List>
                {renderSidebarItems(item.items, nestedLevel, itemKey)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  const sidebarContent = renderSidebarItems(
    filteredSidebarItems,
    0,
    'top-level'
  );

  return (
    <Drawer
      variant="permanent"
      className={`custom-drawer ${
        toggle ? 'opened-drawer' : 'closed-drawer'
      } ${theme}`}
      open={toggle}
      style={{ position: 'relative' }}
    >
      <Grid container className={`sidebar ${theme}`}>
        <List
          sx={{
            maxHeight: `calc(100vh - ${maxHeightValueRef.current}px)`,
          }}
          className="sidebar-content"
        >
          {sidebarContent}
        </List>
      </Grid>
      <MaxOpenedViews></MaxOpenedViews>
    </Drawer>
  );
}
